import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format, isBefore, isSameDay, subHours } from 'date-fns';
import { toast } from 'react-toastify';
import { DepositPaymentStatus } from '@prisma/client';
import SideBarModal from '../modal/SideBarModal';
import { Appointment, FooterAction } from '../../interfaces';
import { createModalData } from '../../utils/modal/appointmentModal';
import useSizeOptions from '../../hooks/tattoos/useSizeOptions';
import useCheckoutForm from '../../hooks/global/useCheckoutForm';
import useUser from '../../hooks/global/useUser';
import usePiercingLocations from '../../hooks/piercings/usePiercingLocations';
import { createFooterAction, openStorageUrl } from '../../helpers/global';
import {
  cancelAppointment,
  createAppointmentChat,
  invalidateAppointmentPayments,
  markAppointmentNoShow,
  markDepositPaid,
  sendAppointmentReminder,
} from '../../services/appointments';
import useSubscriptionModal from '../../hooks/subscriptions/useSubscriptionModal';
import useFeatureGuard from '../../hooks/subscriptions/useFeatureGuard';
import { Feature } from '../../types/subscriptions';
import {
  getCancelAppointmentDepositOptions,
  isAppointmentDepositPaid,
  navigateToChat,
} from '../../helpers/appointments';
import {
  reactivateWaiverRequest,
  requestWaiver,
  sendWaiverReminder,
  createWaiverRequestSession,
} from '../../services/waivers';
import useGetWaivers from '../../hooks/waivers/useGetWaivers';
import useWaiverRequestsTableActions from '../../hooks/waivers/useWaiverRequestsTableActions';
import useConfirm from '../../hooks/useConfirm';
import useLoadingModal from '../../hooks/global/useLoadingModal';
import useAwaitedForm from '../../hooks/useAwaitedForm';
import {
  appointmentRefundDepositSchema,
  AppointmentRefundDepositState,
} from '../../types/appointments';
import TransferAppointmentDepositModal from './TransferAppointmentDepositModal';
import useGetAppointment from '../../hooks/appointments/useGetAppointment';
import { getBusinessShopUrl } from '../../helpers/businesses';
import { getWaiverRequestStatus } from '../../helpers/waivers';
import { ROUTES } from '../../constants/global';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onRefetchAppointments?: () => void | Promise<void>;
  onEdit?: () => void;
  noActions?: boolean;
} & (
  | { appointment: Appointment | null; onRefresh: () => void | Promise<void> }
  | { appointmentId: string }
);

function AppointmentSideBarModal({
  isOpen,
  onClose,
  onRefetchAppointments,
  onEdit,
  noActions,
  ...props
}: Props) {
  const confirm = useConfirm();
  const awaitForm = useAwaitedForm();
  const { openLoadingModal, closeLoadingModal } = useLoadingModal();
  const navigate = useNavigate();
  const piercingLocations = usePiercingLocations();
  const { user } = useUser();
  const { sizeOptions } = useSizeOptions();
  const { openCheckout } = useCheckoutForm();
  const { openSubscriptionModal } = useSubscriptionModal();
  const { data, isPublishedEnabled } = useGetWaivers();
  const { resetWaiversPage } = useWaiverRequestsTableActions({});
  const [isTransferDepositModalOpen, setIsTransferDepositModalOpen] =
    React.useState(false);

  const {
    data: fetchedAppointment,
    refetch: refetchAppointment,
    isPlaceholderData,
  } = useGetAppointment('appointmentId' in props ? props.appointmentId : null);

  const appointment =
    ('appointment' in props
      ? props.appointment
      : isPlaceholderData
      ? null
      : fetchedAppointment) || null;

  const hasWaiversFeature = useFeatureGuard(Feature.WAIVERS);
  const canManageAppointment = useFeatureGuard(Feature.MANAGE_APPOINTMENT);
  const showWaiversFeatures = hasWaiversFeature && isPublishedEnabled;

  const waivers = React.useMemo(() => data?.options ?? [], [data?.options]);

  const refresh = React.useCallback(async () => {
    if ('appointmentId' in props) {
      await refetchAppointment();
    } else if ('appointment' in props) {
      await props.onRefresh?.();
    }
  }, [props, refetchAppointment]);

  const navigateToCustomer = React.useCallback(() => {
    if (!appointment?.customerId) return;
    navigate(`/customers/${appointment.customerId}`);
  }, [navigate, appointment?.customerId]);

  const markDepositAsPaid = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !user
    )
      return;
    try {
      await markDepositPaid(appointment?.appointmentId, {
        depositPaymentMethod: 'CASH',
      });
      await refresh();
      toast.success('Deposit marked as paid');
    } catch (e) {
      toast.error('Error marking deposit as paid');
    }
  }, [canManageAppointment, appointment, user, refresh]);

  const resendBookingLink = React.useCallback(async () => {
    // If the appointment is not an appointment, return
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      appointment.status !== 'SCHEDULED' ||
      !user
    )
      return;
    try {
      await sendAppointmentReminder(appointment);
      toast.success('Reminder sent');
    } catch (e) {
      toast.error('Error sending reminder');
    }
  }, [canManageAppointment, user, appointment]);

  const requestAppointmentWaiver = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !user ||
      !showWaiversFeatures
    )
      return;
    try {
      await requestWaiver({
        appointment,
      });
      resetWaiversPage();
      await refresh();
      toast.success('Waiver requested');
    } catch (e) {
      toast.error('Error requesting waiver');
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    showWaiversFeatures,
    resetWaiversPage,
    refresh,
  ]);

  const resendWaiverReminder = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !appointment.waiverRequest ||
      !user ||
      !isPublishedEnabled
    )
      return;

    try {
      await sendWaiverReminder({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
      toast.success('Waiver link resent');
    } catch (e) {
      toast.error('Error sending waiver link');
    }
  }, [canManageAppointment, isPublishedEnabled, user, appointment]);

  const completeWaiverOnDevice = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !appointment.waiverRequest ||
      !user ||
      !isPublishedEnabled
    )
      return;

    try {
      const session = await createWaiverRequestSession({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
      if (!session?.id) return;
      const url = getBusinessShopUrl(
        user?.business,
        `/waiver-session/${session.id}`,
      );
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Error completing waiver');
    }
  }, [canManageAppointment, isPublishedEnabled, user, appointment]);

  const markAsNoShow = React.useCallback(async () => {
    if (!canManageAppointment || !appointment || !('status' in appointment))
      return;

    try {
      await markAppointmentNoShow(appointment.appointmentId);
      await refresh();
      toast.success('Appointment marked as no-show');
    } catch (e) {
      toast.error('Error marking appointment as no-show');
    }
  }, [canManageAppointment, appointment, refresh]);

  const reactivateWaiver = React.useCallback(async () => {
    if (
      !canManageAppointment ||
      !appointment ||
      !('status' in appointment) ||
      !appointment.businessId ||
      !user ||
      appointment.waiverRequest?.status !== 'CANCELED' ||
      !showWaiversFeatures
    )
      return;

    try {
      await reactivateWaiverRequest({
        businessId: appointment.businessId,
        waiverRequestId: appointment.waiverRequest.id,
      });
      resetWaiversPage();
      await refresh();
      toast.success('Waiver reactivated');
    } catch (e) {
      toast.error('Error reactivating waiver');
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    showWaiversFeatures,
    resetWaiversPage,
    refresh,
  ]);

  const transferDeposit = React.useCallback(async () => {
    setIsTransferDepositModalOpen(true);
  }, []);

  const actions = React.useMemo(() => {
    const moreActions: FooterAction[] = [];
    if (!appointment || noActions) return { moreActions: [] };
    if (
      !!appointment &&
      'status' in appointment &&
      appointment.status !== 'CANCELED'
    ) {
      if (
        appointment.status === 'SCHEDULED' &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.appointmentType !== 'WALK_IN' &&
        appointment.appointmentType !== 'CONSULTATION'
      ) {
        moreActions.push(
          createFooterAction(
            'Send reminder to confirm',
            canManageAppointment ? resendBookingLink : openSubscriptionModal,
          ),
        );
      }
      const isDepositPaid = isAppointmentDepositPaid(appointment);
      if (
        !isDepositPaid &&
        (appointment.depositPrice ?? 0) > 0 &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted
      ) {
        moreActions.push(
          createFooterAction(
            'Mark deposit paid with cash',
            canManageAppointment ? markDepositAsPaid : openSubscriptionModal,
          ),
        );
      }

      const now = new Date();
      const startDate = appointment.startDateTime
        ? new Date(appointment.startDateTime)
        : null;
      // still allow same day after now
      const isValidDay =
        !!startDate && (isSameDay(now, startDate) || isBefore(now, startDate));

      if (
        !appointment?.waiverRequest &&
        appointment?.businessId &&
        isValidDay &&
        showWaiversFeatures &&
        appointment.appointmentType !== 'CONSULTATION'
      ) {
        moreActions.push(
          createFooterAction(
            'Request waiver',
            canManageAppointment
              ? requestAppointmentWaiver
              : openSubscriptionModal,
          ),
        );
      }

      const isCurrentDay = !!startDate && isSameDay(now, startDate);
      if (
        appointment?.waiverRequest &&
        appointment?.businessId &&
        isCurrentDay &&
        isPublishedEnabled &&
        appointment.waiverRequest.status !== 'SIGNED' &&
        appointment.waiverRequest.status !== 'CANCELED'
      ) {
        moreActions.push(
          createFooterAction(
            'Resend waiver link',
            canManageAppointment ? resendWaiverReminder : openSubscriptionModal,
          ),
          createFooterAction(
            'Complete waiver on device',
            canManageAppointment
              ? completeWaiverOnDevice
              : openSubscriptionModal,
          ),
        );
      }

      if (
        appointment?.waiverRequest?.status === 'CANCELED' &&
        isValidDay &&
        showWaiversFeatures
      ) {
        moreActions.push(
          createFooterAction(
            'Reactivate waiver',
            canManageAppointment ? reactivateWaiver : openSubscriptionModal,
          ),
        );
      }

      if (
        (appointment.status === 'SCHEDULED' ||
          appointment.status === 'CONFIRMED') &&
        !appointment.checkoutSessionActive &&
        !appointment.checkoutSessionCompleted &&
        appointment.startDateTime &&
        !appointment.wasNoShow
      ) {
        moreActions.push(
          createFooterAction(
            'Mark as no-show',
            canManageAppointment ? markAsNoShow : openSubscriptionModal,
          ),
        );

        if (
          !isDepositPaid &&
          !appointment.depositPrice &&
          ((!appointment.businessId &&
            appointment.employeeId === user?.userId) ||
            appointment.businessId === user?.business?.id)
        ) {
          moreActions.push(
            createFooterAction(
              'Apply a deposit from clients profile',
              canManageAppointment ? transferDeposit : openSubscriptionModal,
            ),
          );
        }
      }
    }
    return {
      moreActions,
    };
  }, [
    appointment,
    noActions,
    showWaiversFeatures,
    isPublishedEnabled,
    canManageAppointment,
    resendBookingLink,
    openSubscriptionModal,
    markDepositAsPaid,
    requestAppointmentWaiver,
    resendWaiverReminder,
    completeWaiverOnDevice,
    reactivateWaiver,
    markAsNoShow,
    user?.userId,
    user?.business?.id,
    transferDeposit,
  ]);

  const openChat = React.useCallback(async () => {
    if (!canManageAppointment) return;
    if (appointment && user) {
      try {
        openLoadingModal({
          header: 'Opening chat',
          message: 'Please wait while we load the chat...',
        });
        const chatResponse = await createAppointmentChat(appointment, user);
        navigateToChat(navigate, chatResponse);
      } catch {
        // Empty
      } finally {
        closeLoadingModal();
      }
    }
  }, [
    canManageAppointment,
    appointment,
    user,
    openLoadingModal,
    navigate,
    closeLoadingModal,
  ]);

  const cancelAppointmentAction = React.useCallback(async () => {
    if (!canManageAppointment || !appointment) return;

    let depositStatus: DepositPaymentStatus | undefined;

    const isDepositPaid =
      isAppointmentDepositPaid(appointment) && !appointment.isDepositRefunded;
    if (isDepositPaid) {
      const formResult = await awaitForm<AppointmentRefundDepositState>({
        form: {
          title: 'Cancel Appointment',
          submitButtonText: 'Cancel Appointment',
          inputRows: [
            [
              {
                inputType: 'select',
                name: 'depositStatus',
                label:
                  'What do you want to do with the deposit on this appointment?',
                placeholder: 'Select an option',
                isLabelMultiline: true,
                options: getCancelAppointmentDepositOptions(appointment),
                autoWidth: false,
              },
            ],
          ],
          initialValues: {
            depositStatus: '' as DepositPaymentStatus,
          },
          validationSchema: appointmentRefundDepositSchema,
        },
      });
      if (!formResult) return;

      ({ depositStatus } = formResult);
    }

    const confirmed = await confirm({
      title: 'Cancel Appointment',
      message: `Are you sure you want to cancel ${appointment?.employee
        .displayName}'s appointment with ${appointment?.customer
        .firstName} ${appointment?.customer.lastName}${
        appointment?.startDateTime
          ? ` scheduled for ${format(
              new Date(appointment.startDateTime),
              'MM/dd/yyyy',
            )}?`
          : '?'
      }. This action cannot be undone.`,
      negativeText: '',
      positiveText: 'Cancel Appointment',
    });
    if (!confirmed) return;

    try {
      openLoadingModal({
        header: 'Canceling Appointment',
        message:
          'Please wait while we cancel the appointment. This may take a few moments.',
      });

      const cancelResponse = await cancelAppointment(appointment, {
        depositStatus,
      });

      if (cancelResponse) {
        await refresh();
        toast.success('Appointment canceled!');
        await onRefetchAppointments?.();

        if (depositStatus) {
          await invalidateAppointmentPayments(appointment.appointmentId);
        }
      }
    } catch (cancelError) {
      toast.error('Error canceling appointment');
    } finally {
      closeLoadingModal();
    }
  }, [
    canManageAppointment,
    appointment,
    awaitForm,
    confirm,
    openLoadingModal,
    refresh,
    onRefetchAppointments,
    closeLoadingModal,
  ]);

  const footerActions = React.useMemo(() => {
    if (!appointment || appointment.status === 'CANCELED' || noActions)
      return [];
    const tempActions = [
      createFooterAction(
        'Cancel',
        !canManageAppointment
          ? openSubscriptionModal
          : () => cancelAppointmentAction(),
      ),
      createFooterAction(
        'Open Chat',
        !canManageAppointment ? openSubscriptionModal : openChat,
      ),
    ];
    if (onEdit) {
      tempActions.unshift(
        createFooterAction(
          'Edit',
          !canManageAppointment ? openSubscriptionModal : () => onEdit(),
        ),
      );
    }
    return tempActions;
  }, [
    appointment,
    canManageAppointment,
    openSubscriptionModal,
    openChat,
    onEdit,
    cancelAppointmentAction,
    noActions,
  ]);

  const onWaiverPress = React.useCallback(async () => {
    if (!appointment) return;
    const { waiverRequest } = appointment;
    if (!waiverRequest || !appointment.startDateTime) return;

    const waiverStatus = getWaiverRequestStatus({
      appointment,
      waivers,
    });

    const isPendingEmployee = waiverStatus === 'pendingEmployee';
    const isSent = waiverRequest.status === 'SENT';
    const isQueued = waiverRequest.status === 'QUEUED';
    const isComplete = waiverRequest.status === 'SIGNED';
    const isCanceled = waiverRequest.status === 'CANCELED';
    const willNeverComplete =
      appointment.status === 'CANCELED' && (isQueued || isSent);

    if (!isPendingEmployee && isComplete && waiverRequest.signedFileLink) {
      openStorageUrl(waiverRequest.signedFileLink);
      return;
    }

    const hourBeforeAppt = subHours(new Date(appointment.startDateTime), 1);
    const queuedTime = format(new Date(hourBeforeAppt), 'h:mma');
    const queuedDate = format(new Date(hourBeforeAppt), 'EEEE, MMMM d');

    const message =
      waiverStatus === 'pendingEmployee'
        ? 'Customer has filled in the waiver, but the service provider still needs to finalize the waiver'
        : willNeverComplete
        ? 'Waiver was not completed by the customer'
        : isSent
        ? 'Waiver has been sent to customer for signature'
        : isQueued
        ? `Waiver will be sent to the customer for their signature 1 hour before the appointment at ${queuedTime} on ${queuedDate}`
        : isComplete
        ? 'Waiver has been completed by the customer'
        : isCanceled
        ? 'Waiver has been canceled'
        : 'Waiver has not yet been completed by the customer';

    const positiveText = isPendingEmployee ? 'Finalize Waiver' : 'Done';
    const negativeText = isPendingEmployee ? 'Back' : '';

    const confirmed = await confirm({
      dismissible: true,
      title: 'Waiver',
      message,
      positiveText,
      negativeText,
      isSmall: true,
      hideCloseButton: true,
    });

    if (confirmed && isPendingEmployee && waiverRequest.signedFileLink) {
      navigate(`${ROUTES.waiverRequest}/${waiverRequest.id}`);
    }
  }, [confirm, appointment, waivers, navigate]);

  if (!('appointmentId' in props) && !appointment) return null;

  const modalData = appointment
    ? createModalData({
        appointment,
        loadedUser: user!,
        navigateToCustomer,
        openCheckoutModal: () => openCheckout(appointment),
        sizeOptions,
        piercingLocations,
        refreshModal: refresh,
        waiverButtonAction: onWaiverPress,
        waivers,
      })
    : null;

  return (
    <>
      <SideBarModal
        name={modalData?.name || 'Appointment Detail'}
        modalOpen={isOpen}
        sections={modalData?.sections || []}
        isLoading={!modalData}
        setModalOpen={onClose}
        moreActions={modalData ? actions.moreActions : []}
        footerActions={modalData ? footerActions : []}
      />

      {isTransferDepositModalOpen && !!appointment && (
        <TransferAppointmentDepositModal
          isOpen
          onClose={() => setIsTransferDepositModalOpen(false)}
          onRefresh={refresh}
          appointment={appointment}
        />
      )}
    </>
  );
}

export default AppointmentSideBarModal;
